import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { CalendarsService } from '@modules/calendar-app/services/calendars.service';
import { Calendar } from '@modules/calendar-app/types/calendar';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { CalendarsFilters } from '@modules/calendar-app/types/calendars-filters';
import { VirtualFolder } from '@modules/calendar-app/types/virtual-folder';
import { BaseStitchListComponent, StitchListItem } from '@modules/common/components/list/base-stitch-list.component';
import { BaseAppStateService } from '@modules/common/services/base-app-state.service';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { CalendarComponent } from '@modules/settings/components/calendar/calendar/calendar.component';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-calendars-list',
  templateUrl: './calendars-list.component.html',
  styleUrls: ['./calendars-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: false,
})
export class CalendarsListComponent extends BaseStitchListComponent<Calendar> implements OnInit, OnChanges, OnDestroy {
  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** Container treatment forwarded to every card; the knowledge panel asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The knowledge panel draws its results as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  @Input()
  public filters: Partial<CalendarsFilters> = {};

  @Input()
  public withTags = false;
  public readonly tenantItems = input<ReadonlyArray<{ readonly accountId?: string }>>();
  @Input()
  public withKnots = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickCalendar = new EventEmitter<Calendar>();
  @Output()
  public readonly openCalendar = new EventEmitter<Calendar>();
  @Output()
  public readonly doubleClickCalendar = new EventEmitter<Calendar>();
  @Output()
  public readonly openEvent = new EventEmitter<CalendarEvent>();

  @ViewChildren(CalendarComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  public override items: StitchListItem<Calendar>[] = [];

  public showCountView = new BehaviorSubject(true);

  private readonly calendarsService = inject(CalendarsService);
  private readonly eventsStateService =
    inject<BaseAppStateService<Calendar, CalendarEvent, VirtualFolder>>(BaseAppStateService);
  private readonly linkedInfoService = inject(LinkedInfoService);

  protected override getStitchType(): StitchType {
    return StitchType.CALENDAR;
  }

  public override ngOnInit() {
    merge(
      // Global refresh button
      this.eventsStateService.getRefreshAll(),
      // Subscribe for linked info updates
      this.linkedInfoService.getRefresh(),
      this.calendarsService.getRefresh(),
    )
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });

    super.ngOnInit();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('filters' in changes || (changes.tenantItems && !changes.tenantItems.isFirstChange())) {
      this.resetItems();
    }
  }

  public override getItems(offset: number, limit: number): Observable<{ items: Calendar[]; count: number }> {
    return this.calendarsService.search(
      {
        ...this.filters,
        archived: this.filters?.archived ?? false,
        deleted: this.filters?.deleted ?? false,
        limit,
        offset,
      },
      { withTags: this.withTags, withKnots: this.withKnots, withConnections: false, knotsSource: this.knotsSource,
        tenantItems: this.tenantItems() },
    );
  }

  public handleDoubleClickItem(calendar: Calendar) {
    this.doubleClickCalendar.emit(calendar);
  }

  public handleOpenSelected(event: SelectableOpenEvent<Calendar>): void {
    this.clickCalendar.emit(event.item.data);
  }
}
